"use client"
import { useState } from "react"
import { useRouter } from "next/navigation"
import { Eye, EyeOff } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { COUNTRIES } from "@/lib/countries"

type Props = {
  name: string | null
  city: string | null
  country: string | null
  email?: string | null
}

export function AccountSettingsForm({ name, city, country, email }: Props) {
  const router = useRouter()
  const [form, setForm] = useState({
    name: name ?? "",
    city: city ?? "",
    country: country ?? "",
  })
  const [currentEmail, setCurrentEmail] = useState(email ?? "")
  const [emailForm, setEmailForm] = useState({ newEmail: "", currentPassword: "" })
  const [emailSuccess, setEmailSuccess] = useState(false)
  const [emailError, setEmailError] = useState("")
  const [savingEmail, setSavingEmail] = useState(false)
  const [showEmailPw, setShowEmailPw] = useState(false)
  const [passwords, setPasswords] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  })
  const [saving, setSaving] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState("")
  const [pwSuccess, setPwSuccess] = useState(false)
  const [pwError, setPwError] = useState("")
  const [savingPw, setSavingPw] = useState(false)
  const [showCurrent, setShowCurrent] = useState(false)
  const [showNew, setShowNew] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    setSuccess(false)
    setError("")
    try {
      const res = await fetch("/api/account", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name || undefined,
          city: form.city || null,
          country: form.country || null,
        }),
      })
      if (!res.ok) {
        const data = await res.json()
        setError(typeof data.error === "string" ? data.error : "Failed to save")
      } else {
        setSuccess(true)
        router.refresh()
      }
    } catch {
      setError("Network error")
    } finally {
      setSaving(false)
    }
  }

  async function handleEmailSubmit(e: React.FormEvent) {
    e.preventDefault()
    setEmailError("")
    setEmailSuccess(false)
    setSavingEmail(true)
    try {
      const res = await fetch("/api/account", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: emailForm.newEmail,
          currentPassword: emailForm.currentPassword,
        }),
      })
      const data = await res.json()
      if (!res.ok) {
        setEmailError(typeof data.error === "string" ? data.error : "Failed to update email")
      } else {
        setEmailSuccess(true)
        setCurrentEmail(data.email ?? emailForm.newEmail)
        setEmailForm({ newEmail: "", currentPassword: "" })
        router.refresh()
      }
    } catch {
      setEmailError("Network error")
    } finally {
      setSavingEmail(false)
    }
  }

  async function handlePasswordSubmit(e: React.FormEvent) {
    e.preventDefault()
    setPwError("")
    setPwSuccess(false)
    if (passwords.newPassword !== passwords.confirmPassword) {
      setPwError("New passwords don't match")
      return
    }
    if (passwords.newPassword.length < 8) {
      setPwError("New password must be at least 8 characters")
      return
    }
    setSavingPw(true)
    try {
      const res = await fetch("/api/account", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          currentPassword: passwords.currentPassword,
          newPassword: passwords.newPassword,
        }),
      })
      const data = await res.json()
      if (!res.ok) {
        setPwError(typeof data.error === "string" ? data.error : "Failed to update password")
      } else {
        setPwSuccess(true)
        setPasswords({ currentPassword: "", newPassword: "", confirmPassword: "" })
      }
    } catch {
      setPwError("Network error")
    } finally {
      setSavingPw(false)
    }
  }

  return (
    <div className="space-y-8">
      {/* Profile info */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="text-xs font-medium block mb-1">Display name</label>
          <Input
            type="text"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Your name"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium block mb-1">City</label>
            <Input
              type="text"
              value={form.city}
              onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
              placeholder="e.g. Brooklyn"
            />
          </div>
          <div>
            <label className="text-xs font-medium block mb-1">Country</label>
            <select
              value={form.country}
              onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}
              className="w-full border border-[var(--border)] rounded-[var(--radius)] px-3 py-2 text-sm bg-[var(--background)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)]"
            >
              <option value="">— select —</option>
              {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
        {error && <p className="text-xs text-red-600">{error}</p>}
        {success && <p className="text-xs text-green-600">Saved!</p>}
        <Button type="submit" size="sm" disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </Button>
      </form>

      {/* Email change */}
      <div className="border-t border-[var(--border)] pt-6">
        <h3 className="text-sm font-semibold mb-1">Change email</h3>
        <p className="text-xs text-[var(--muted-foreground)] mb-4">
          Signed in as <strong>{currentEmail || "—"}</strong>. Your email is what you use to log in — alerts and account emails go there too.
        </p>
        <form onSubmit={handleEmailSubmit} className="space-y-3">
          <div>
            <label className="text-xs font-medium block mb-1">New email</label>
            <Input
              type="email"
              value={emailForm.newEmail}
              onChange={(e) => setEmailForm((f) => ({ ...f, newEmail: e.target.value }))}
              autoComplete="email"
              placeholder="you@example.com"
              required
            />
          </div>
          <div>
            <label className="text-xs font-medium block mb-1">Current password</label>
            <div className="relative">
              <Input
                type={showEmailPw ? "text" : "password"}
                value={emailForm.currentPassword}
                onChange={(e) => setEmailForm((f) => ({ ...f, currentPassword: e.target.value }))}
                autoComplete="current-password"
                required
                className="pr-10"
              />
              <button type="button" onClick={() => setShowEmailPw((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--muted-foreground)] hover:text-[var(--foreground)]" aria-label={showEmailPw ? "Hide" : "Show"}>
                {showEmailPw ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>
          {emailError && <p className="text-xs text-red-600">{emailError}</p>}
          {emailSuccess && <p className="text-xs text-green-600">Email updated! Use the new address next time you sign in.</p>}
          <Button type="submit" size="sm" disabled={savingEmail}>
            {savingEmail ? "Updating…" : "Update email"}
          </Button>
        </form>
      </div>

      {/* Password change */}
      <div className="border-t border-[var(--border)] pt-6">
        <h3 className="text-sm font-semibold mb-4">Change password</h3>
        <form onSubmit={handlePasswordSubmit} className="space-y-3">
          <div>
            <label className="text-xs font-medium block mb-1">Current password</label>
            <div className="relative">
              <Input
                type={showCurrent ? "text" : "password"}
                value={passwords.currentPassword}
                onChange={(e) => setPasswords((p) => ({ ...p, currentPassword: e.target.value }))}
                autoComplete="current-password"
                className="pr-10"
              />
              <button type="button" onClick={() => setShowCurrent((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--muted-foreground)] hover:text-[var(--foreground)]" aria-label={showCurrent ? "Hide" : "Show"}>
                {showCurrent ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium block mb-1">New password</label>
            <div className="relative">
              <Input
                type={showNew ? "text" : "password"}
                value={passwords.newPassword}
                onChange={(e) => setPasswords((p) => ({ ...p, newPassword: e.target.value }))}
                autoComplete="new-password"
                placeholder="8+ characters"
                className="pr-10"
              />
              <button type="button" onClick={() => setShowNew((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--muted-foreground)] hover:text-[var(--foreground)]" aria-label={showNew ? "Hide" : "Show"}>
                {showNew ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium block mb-1">Confirm new password</label>
            <div className="relative">
              <Input
                type={showConfirm ? "text" : "password"}
                value={passwords.confirmPassword}
                onChange={(e) => setPasswords((p) => ({ ...p, confirmPassword: e.target.value }))}
                autoComplete="new-password"
                className="pr-10"
              />
              <button type="button" onClick={() => setShowConfirm((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--muted-foreground)] hover:text-[var(--foreground)]" aria-label={showConfirm ? "Hide" : "Show"}>
                {showConfirm ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>
          {pwError && <p className="text-xs text-red-600">{pwError}</p>}
          {pwSuccess && <p className="text-xs text-green-600">Password updated!</p>}
          <Button type="submit" size="sm" disabled={savingPw}>
            {savingPw ? "Updating…" : "Update password"}
          </Button>
        </form>
      </div>
    </div>
  )
}
